import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader2, Gauge } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const colourMap = {
  green: { stroke: '#10b981', text: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200' },
  blue: { stroke: '#3b82f6', text: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200' },
  yellow: { stroke: '#f59e0b', text: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' },
  red: { stroke: '#ef4444', text: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' },
};

const Gauge180 = ({ score, colour, size = 160, testId }) => {
  const c = colourMap[colour] || colourMap.yellow;
  const r = size / 2 - 12;
  const cx = size / 2;
  const cy = size / 2;
  const clamped = Math.max(0, Math.min(100, score || 0));
  // Semi-circle path from (12, size/2) sweeping to (size-12, size/2)
  const half = Math.PI * r; // length of semicircle
  const filled = (clamped / 100) * half;
  const dash = `${filled} ${half}`;
  return (
    <div className="relative" style={{ width: size, height: size / 2 + 12 }} data-testid={testId}>
      <svg width={size} height={size / 2 + 12} viewBox={`0 0 ${size} ${size / 2 + 12}`}>
        {/* background arc */}
        <path
          d={`M 12 ${cy} A ${r} ${r} 0 0 1 ${size - 12} ${cy}`}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth="10"
          strokeLinecap="round"
        />
        {/* filled arc */}
        <path
          d={`M 12 ${cy} A ${r} ${r} 0 0 1 ${size - 12} ${cy}`}
          fill="none"
          stroke={c.stroke}
          strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray={dash}
          style={{ transition: 'stroke-dasharray 800ms ease-out' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-end pb-1">
        <p className={`text-2xl font-bold ${c.text}`}>{Math.round(clamped)}%</p>
      </div>
    </div>
  );
};

const SubjectCard = ({ s, testId }) => {
  const c = colourMap[s.colour] || colourMap.yellow;
  return (
    <div className={`rounded-xl border p-4 ${c.bg} ${c.border} flex flex-col items-center`} data-testid={testId}>
      <p className="text-xs uppercase tracking-wide text-gray-600 mb-1">{s.subject}</p>
      <Gauge180 score={s.readiness_score} colour={s.colour} size={140} testId={`${testId}-gauge`} />
      <p className={`font-semibold ${c.text} mt-1`}>{s.label}</p>
      <p className="text-[11px] text-gray-500 mt-1">{s.chapters_covered} chapter{s.chapters_covered !== 1 ? 's' : ''} · {s.attempts_considered} attempts</p>
    </div>
  );
};

const ExamReadinessGauge = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/student/exam-readiness`);
        setData(res.data);
      } catch (e) {
        console.error('readiness load error', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-8 flex items-center justify-center" data-testid="readiness-loading">
        <Loader2 className="animate-spin text-blue-500" size={22} />
      </div>
    );
  }

  const overall = data?.overall || { readiness_score: 0, label: 'At Risk', colour: 'red' };
  const subjects = data?.subjects || [];
  const c = colourMap[overall.colour] || colourMap.yellow;

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="exam-readiness">
      <div className="px-6 py-4 bg-gradient-to-r from-slate-800 to-slate-900 text-white flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Gauge size={22} />
          <div>
            <h3 className="font-semibold text-lg">Exam Readiness</h3>
            <p className="text-slate-300 text-xs">Based on your last 90 days of Topic Tests</p>
          </div>
        </div>
        <div className="text-right">
          <p className={`text-4xl font-bold ${c.text}`} data-testid="readiness-overall-score">{Math.round(overall.readiness_score)}%</p>
          <p className={`text-xs uppercase tracking-wide font-semibold ${c.text}`}>{overall.label}</p>
        </div>
      </div>
      <div className="p-4">
        {subjects.length === 0 ? (
          <p className="text-center text-gray-400 py-8 text-sm">
            Attempt Topic Tests to unlock per-subject readiness.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {subjects.map((s, i) => (
              <SubjectCard key={`${s.subject}-${i}`} s={s} testId={`readiness-subject-${s.subject.toLowerCase().replace(/\s+/g, '-')}`} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExamReadinessGauge;
